// `slots`: the meeting times `calendar_propose_slots` returned, in the owner's zone, with any
// hold or booking MeeMail already made for this card next to each one (GET
// /api/calendar/cards/:id/holds). "Pick other times" asks the calendar for alternatives and can
// only hand them to the operator as an instruction: the UI never turns a slots block into a
// hold, and holding anything still goes through the approve strip.
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CalendarClock, CalendarSearch } from "lucide-react";
import { toast } from "sonner";
import type { CalendarSlotView, SlotsPreviewResponse } from "@shared/api";
import type { CardBlock } from "@shared/core";
import { isApiError } from "@web/lib/api";
import { calendarApi } from "@web/lib/calendarApi";
import { useCardHolds } from "@web/lib/calendarQueries";
import { cn } from "@web/lib/cn";
import { feedApi } from "@web/lib/feedApi";
import { slotLabel, slotMinutes, viewerZone, zoneNote, zoneOf } from "@web/lib/slots";
import { zoneLabel } from "@web/lib/timeZones";
import { registerUndo } from "@web/lib/undoRegistry";
import { Button } from "@web/ui/Button";
import { cancelWork } from "../cardOps";
import {
  holdFor,
  holdsOutside,
  holdWords,
  otherTimesInstruction,
  type HoldWords,
} from "./slotsWords";

/** What the block needs to know about its card (absent when rendered without one). */
export interface SlotsContext {
  cardId: string;
  feedId: string;
  accountId: string | null;
  /** Bumps when the card changes, so holds are read again after work runs. */
  updatedAt: string;
  /** The card is in review, so asking for other times makes sense. */
  actionable: boolean;
}

const TONE: Record<HoldWords["tone"], string> = {
  calm: "border-calm-line bg-calm-soft text-ink-2",
  working: "border-line bg-sunk text-ink-2",
  muted: "border-line bg-bg text-muted",
  attention: "border-note-line bg-note text-note-ink",
};

function HoldBadge({ words }: { words: HoldWords }) {
  return (
    <span
      data-testid="hold-state"
      className={cn(
        "inline-flex shrink-0 items-center rounded-full border px-2 text-[11px] leading-5 font-medium",
        TONE[words.tone],
      )}
    >
      {words.label}
    </span>
  );
}

function OtherTimes({
  context,
  zone,
  durationMin,
  after,
  onClose,
}: {
  context: SlotsContext & { accountId: string };
  zone: string;
  durationMin: number;
  after: string | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [result, setResult] = useState<SlotsPreviewResponse | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [sending, setSending] = useState(false);

  const load = async () => {
    setFailed(null);
    setResult(null);
    try {
      const res = await calendarApi.slots({
        accountId: context.accountId,
        count: 3,
        ...(durationMin ? { durationMin } : {}),
        ...(after ? { earliest: after } : {}),
      });
      setResult(res);
      setPicked(new Set(res.slots.map((s) => s.start)));
    } catch (error) {
      setFailed(
        isApiError(error, "invalid_request")
          ? (error.serverMessage ?? "The calendar isn't connected for this account.")
          : "Couldn't read the calendar just now.",
      );
    }
  };
  // Load once on open (the panel is remounted each time it opens).
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chosen = (result?.slots ?? []).filter((s) => picked.has(s.start));
  const ask = async () => {
    if (!result || !chosen.length || sending) return;
    setSending(true);
    try {
      const work = await feedApi.instruct(context.feedId, {
        instruction: otherTimesInstruction(chosen, result.timeZone),
        target: { kind: "card", cardId: context.cardId },
      });
      registerUndo({
        message: "Asked Claude to propose these times instead",
        undo: () => cancelWork(queryClient, work.id),
        doneMessage: "Cancelled. The card is back for review.",
        tooLateMessage: "Too late to cancel: Claude already started.",
      });
      void queryClient.invalidateQueries({ queryKey: ["work"] });
      void queryClient.invalidateQueries({ queryKey: ["board"] });
      void queryClient.invalidateQueries({ queryKey: ["card", context.cardId] });
      onClose();
    } catch (error) {
      toast(
        isApiError(error, "not_available")
          ? "Claude isn't wired up yet. Nothing was changed."
          : "Couldn't queue that. Nothing was changed.",
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      role="group"
      aria-label="Other times"
      className="flex flex-col gap-2 rounded-lg border border-line bg-bg px-3 py-2.5"
    >
      <p className="text-sm font-medium text-ink">
        Other free times{result ? ` in ${zoneLabel(zone)}` : ""}
      </p>
      {failed ? (
        <p className="text-sm text-muted">{failed}</p>
      ) : !result ? (
        <p className="text-sm text-muted" aria-busy>
          Looking at your calendar…
        </p>
      ) : result.slots.length ? (
        <ul className="flex flex-col gap-1.5">
          {result.slots.map((s: CalendarSlotView) => (
            <li key={s.start}>
              <label className="inline-flex items-center gap-2 text-sm text-ink-2">
                <input
                  type="checkbox"
                  checked={picked.has(s.start)}
                  onChange={(e) =>
                    setPicked((p) => {
                      const next = new Set(p);
                      if (e.target.checked) next.add(s.start);
                      else next.delete(s.start);
                      return next;
                    })
                  }
                  className="size-4 accent-[var(--accent)]"
                />
                {s.label || slotLabel(s, result.timeZone)}
              </label>
            </li>
          ))}
        </ul>
      ) : null}
      {result?.notes.map((n) => (
        <p key={n} className="text-sm text-muted">
          {n}
        </p>
      ))}
      <div className="flex flex-wrap items-center gap-2">
        {result && result.slots.length ? (
          <Button
            size="sm"
            variant="primary"
            busy={sending}
            disabled={!chosen.length}
            onClick={() => void ask()}
          >
            Ask Claude to propose these
          </Button>
        ) : null}
        {failed ? (
          <Button size="sm" onClick={() => void load()}>
            Try again
          </Button>
        ) : null}
        <Button size="sm" variant="ghost" onClick={onClose}>
          Never mind
        </Button>
      </div>
      <p className="text-xs text-muted">
        Claude rewrites the card with these times. Nothing is held until you approve it.
      </p>
    </div>
  );
}

export function SlotsBlock({
  block,
  context,
}: {
  block: CardBlock;
  context?: SlotsContext | undefined;
}) {
  const slots = block.slots ?? [];
  const zone = zoneOf(slots) ?? "UTC";
  const note = zoneNote(zone, viewerZone());
  const holds = useCardHolds(context?.cardId ?? null, context?.updatedAt ?? null);
  const list = holds.data ?? [];
  const outside = holdsOutside(list, slots).filter((h) => h.status !== "released");
  const [other, setOther] = useState(false);
  const canPick = !!context?.actionable && !!context.accountId;
  const last = slots.length ? slots[slots.length - 1]!.end : null;

  return (
    <section aria-label={block.label ?? "Proposed times"} data-testid="slots-block">
      <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h3 className="mm-kicker">{block.label ?? "Times that work"}</h3>
        {note ? <span className="text-xs text-muted">{note}</span> : null}
      </div>
      {block.text ? <p className="mb-2 text-sm text-ink-2">{block.text}</p> : null}
      <ol className="flex flex-col gap-1.5">
        {slots.map((s) => {
          const hold = holdFor(list, s);
          return (
            <li
              key={`${s.start}/${s.end}`}
              className="flex items-center gap-2.5 rounded-lg border border-line bg-bg px-3 py-2 text-ink"
            >
              <CalendarClock aria-hidden className="size-4 shrink-0 text-muted" />
              <span className="min-w-0 flex-1 tabular-nums" data-testid="slot-label">
                {slotLabel(s, s.tz) || `${s.start} – ${s.end}`}
              </span>
              {hold ? <HoldBadge words={holdWords(hold)} /> : null}
            </li>
          );
        })}
      </ol>
      {outside.length ? (
        <div className="mt-2 text-sm text-muted">
          <p>Also on your calendar from this card:</p>
          <ul className="mt-1 flex flex-col gap-1">
            {outside.map((h) => (
              <li key={h.id} className="flex items-center gap-2">
                <span className="tabular-nums">{slotLabel(h, h.tz)}</span>
                <HoldBadge words={holdWords(h)} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {canPick && context?.accountId ? (
        <div className="mt-2">
          {other ? (
            <OtherTimes
              context={{ ...context, accountId: context.accountId }}
              zone={zone}
              durationMin={slots[0] ? slotMinutes(slots[0]) : 0}
              after={last}
              onClose={() => setOther(false)}
            />
          ) : (
            <button
              type="button"
              onClick={() => setOther(true)}
              className="inline-flex items-center gap-1.5 text-sm text-accent-text underline underline-offset-2"
            >
              <CalendarSearch aria-hidden className="size-4" />
              Pick other times
            </button>
          )}
        </div>
      ) : null}
    </section>
  );
}
